import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AppShell } from "@/components/layout/AppShell";
import { CampusesAPI } from "@/lib/sprint2-api";
import { InfraAPI, CARD_SPECS } from "@/lib/infra-api";
import { CampusProfilePanel } from "@/components/infra/CampusProfilePanel";
import { BuildingsPanel } from "@/components/infra/BuildingsPanel";
import { FacilitiesPanel } from "@/components/infra/FacilitiesPanel";
import { DocumentsPanel } from "@/components/infra/DocumentsPanel";
import { MaintenancePanel } from "@/components/infra/MaintenancePanel";
import { ReportsPanel } from "@/components/infra/ReportsPanel";
import { ComingSoonPanel } from "@/components/infra/ComingSoonPanel";
import {
  ACADEMIC_FACILITIES, SPORTS_FACILITIES, STUDENT_FACILITIES,
  SMART_CAMPUS_ITEMS, SAFETY_ITEMS, UTILITIES, ASSET_KINDS,
} from "@/lib/facility-catalog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import {
  Landmark, Building2, GraduationCap, Trophy, Users, Wifi, Shield, Zap,
  Monitor, FileText, Wrench, BarChart3, ChevronRight, Sparkles, CheckCircle2,
  AlertCircle, ArrowUpRight, Layers, DoorOpen, BookOpen, HardHat, Boxes,
  ShieldAlert, Clock, TrendingUp,
} from "lucide-react";
import { cn } from "@/lib/utils";

const ICONS = {
  Landmark, Building2, GraduationCap, Trophy, Users, Wifi, Shield, Zap,
  Monitor, FileText, Wrench, BarChart3,
};

const COLOR_STYLES = {
  sky:      { bg: "bg-sky-50",      text: "text-sky-600",      ring: "ring-sky-200",      bar: "bg-sky-500" },
  indigo:   { bg: "bg-indigo-50",   text: "text-indigo-600",   ring: "ring-indigo-200",   bar: "bg-indigo-500" },
  violet:   { bg: "bg-violet-50",   text: "text-violet-600",   ring: "ring-violet-200",   bar: "bg-violet-500" },
  amber:    { bg: "bg-amber-50",    text: "text-amber-600",    ring: "ring-amber-200",    bar: "bg-amber-500" },
  emerald:  { bg: "bg-emerald-50",  text: "text-emerald-600",  ring: "ring-emerald-200",  bar: "bg-emerald-500" },
  cyan:     { bg: "bg-cyan-50",     text: "text-cyan-600",     ring: "ring-cyan-200",     bar: "bg-cyan-500" },
  rose:     { bg: "bg-rose-50",     text: "text-rose-600",     ring: "ring-rose-200",     bar: "bg-rose-500" },
  yellow:   { bg: "bg-yellow-50",   text: "text-yellow-600",   ring: "ring-yellow-200",   bar: "bg-yellow-500" },
  slate:    { bg: "bg-slate-100",   text: "text-slate-600",    ring: "ring-slate-200",    bar: "bg-slate-500" },
  teal:     { bg: "bg-teal-50",     text: "text-teal-600",     ring: "ring-teal-200",     bar: "bg-teal-500" },
  orange:   { bg: "bg-orange-50",   text: "text-orange-600",   ring: "ring-orange-200",   bar: "bg-orange-500" },
  fuchsia:  { bg: "bg-fuchsia-50",  text: "text-fuchsia-600",  ring: "ring-fuchsia-200",  bar: "bg-fuchsia-500" },
};

export default function Infrastructure() {
  const campusesQ = useQuery({ queryKey: ["infra-campuses"], queryFn: () => CampusesAPI.list() });
  const campuses = campusesQ.data || [];
  const [campusId, setCampusId] = useState(null);
  const activeCampus = useMemo(() => {
    if (!campuses.length) return null;
    return campuses.find((c) => c.id === campusId) || campuses[0];
  }, [campuses, campusId]);
  const activeId = activeCampus?.id;

  const dashQ = useQuery({
    queryKey: ["infra-dashboard", activeId],
    queryFn: () => InfraAPI.dashboard(activeId),
    enabled: !!activeId,
  });

  const [openCard, setOpenCard] = useState(null);
  const openPanel = (key) => setOpenCard(key);
  const close = () => setOpenCard(null);

  const dash = dashQ.data;
  const completions = dash?.completion_by_section || {};
  const kpis = dash?.kpis || {};

  return (
    <AppShell>
      <div className="p-6 max-w-[1400px] mx-auto space-y-6" data-testid="infrastructure-page">
        {/* Header */}
        <header className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="h-8 w-8 rounded-xl bg-indigo-600/10 flex items-center justify-center">
                <Landmark className="h-4 w-4 text-indigo-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Campus Infrastructure</h1>
            </div>
            <p className="text-sm text-slate-500 max-w-2xl">
              The single source of truth for every building, room, facility and asset. Powers Exam,
              Timetable, Attendance, Hostel, Transport, Visitor and Campus 360° modules.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <div className="w-64">
              <Select value={activeId || ""} onValueChange={setCampusId}>
                <SelectTrigger data-testid="campus-picker">
                  <SelectValue placeholder="Choose campus" />
                </SelectTrigger>
                <SelectContent>
                  {campuses.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </header>

        {!activeCampus ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-slate-500">
            {campusesQ.isLoading ? "Loading campuses…" : "No campus available. Add one from the Campuses page first."}
          </div>
        ) : (
          <>
            {/* Progress + KPI strip */}
            <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="lg:col-span-1 rounded-2xl bg-gradient-to-br from-indigo-600 via-indigo-500 to-violet-500 text-white p-6 shadow-md">
                <div className="text-xs uppercase tracking-widest opacity-80 font-semibold">Overall Progress</div>
                <div className="mt-2 flex items-baseline gap-2">
                  <div className="text-5xl font-bold" data-testid="overall-progress">{dash?.overall_completion ?? 0}%</div>
                  <div className="text-sm opacity-80">complete</div>
                </div>
                <div className="mt-4 h-2 bg-white/20 rounded-full overflow-hidden">
                  <div className="h-full bg-white rounded-full transition-all" style={{ width: `${dash?.overall_completion ?? 0}%` }} />
                </div>
                <div className="mt-6 grid grid-cols-3 gap-2 text-xs">
                  <ProgressPill label="Not started" count={sectionsByState(completions, "not_started")} tone="light" />
                  <ProgressPill label="In progress" count={sectionsByState(completions, "in_progress")} tone="light" />
                  <ProgressPill label="Done" count={sectionsByState(completions, "done")} tone="light" />
                </div>
              </div>

              <div className="lg:col-span-2 grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Kpi icon={Building2} label="Buildings" value={kpis.buildings} tone="indigo" />
                <Kpi icon={Layers} label="Floors" value={kpis.floors} tone="violet" />
                <Kpi icon={DoorOpen} label="Rooms" value={kpis.rooms} tone="sky" />
                <Kpi icon={GraduationCap} label="Classrooms" value={kpis.classrooms} tone="emerald" />
                <Kpi icon={HardHat} label="Laboratories" value={kpis.laboratories} tone="amber" />
                <Kpi icon={BookOpen} label="Libraries" value={kpis.libraries} tone="teal" />
                <Kpi icon={Boxes} label="Assets" value={kpis.assets} tone="slate" />
                <Kpi icon={CheckCircle2} label="Exam-ready rooms" value={kpis.exam_ready_rooms} tone="fuchsia" />
              </div>
            </section>

            {/* Compliance & Maintenance heartbeat */}
            <ComplianceStrip
              expiring={dash?.expiring_documents}
              maintenance={dash?.maintenance_by_status}
              onOpen={openPanel}
            />

            {/* Recommendations */}
            {dash?.recommendations?.length > 0 && (
              <section className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="recommendations">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="h-4 w-4 text-violet-500" />
                  <h2 className="text-sm font-semibold text-slate-900">Smart recommendations</h2>
                </div>
                <ul className="space-y-2">
                  {dash.recommendations.map((r, i) => (
                    <li key={i} className="flex items-start gap-3 px-3 py-2 rounded-lg bg-slate-50">
                      <AlertCircle className={cn(
                        "h-4 w-4 mt-0.5 shrink-0",
                        r.priority === "high" && "text-rose-500",
                        r.priority === "medium" && "text-amber-500",
                        r.priority === "low" && "text-slate-400",
                      )} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-slate-800">{r.message}</div>
                        <div className="text-xs text-slate-500 uppercase tracking-wide">{r.priority}</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => openPanel(r.section)}
                        className="text-xs px-2 py-1 rounded-md bg-white border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1"
                        data-testid={`reco-jump-${r.section}`}
                      >
                        Configure <ArrowUpRight className="h-3 w-3" />
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* 12 cards */}
            <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {CARD_SPECS.map((spec) => {
                const pct = completions[spec.key] || 0;
                const Icon = ICONS[spec.icon] || Sparkles;
                const styles = COLOR_STYLES[spec.color] || COLOR_STYLES.slate;
                return (
                  <button
                    key={spec.key}
                    type="button"
                    onClick={() => openPanel(spec.key)}
                    className={cn(
                      "group text-left rounded-2xl bg-white p-5 border border-slate-200",
                      "hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200"
                    )}
                    data-testid={`card-${spec.key}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className={cn("h-10 w-10 rounded-xl flex items-center justify-center", styles.bg)}>
                        <Icon className={cn("h-5 w-5", styles.text)} />
                      </div>
                      <StatusBadge pct={pct} />
                    </div>
                    <div className="mt-4">
                      <div className="text-base font-semibold text-slate-900">{spec.label}</div>
                      <p className="text-xs text-slate-500 mt-1 line-clamp-2">{spec.summary}</p>
                    </div>
                    <div className="mt-4">
                      <div className="flex items-center justify-between text-[11px] text-slate-500 mb-1.5">
                        <span>Setup completion</span>
                        <span className="font-medium text-slate-700" data-testid={`pct-${spec.key}`}>{pct}%</span>
                      </div>
                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className={cn("h-full rounded-full transition-all", styles.bar)} style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                    <div className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-slate-700 group-hover:text-indigo-600 transition-colors">
                      Configure <ChevronRight className="h-3.5 w-3.5" />
                    </div>
                  </button>
                );
              })}
            </section>
          </>
        )}
      </div>

      {/* Panels */}
      <CampusProfilePanel open={openCard === "campus_profile"} onClose={close} campusId={activeId} />
      <BuildingsPanel open={openCard === "buildings"} onClose={close} campusId={activeId} campusLabel={activeCampus?.name} />
      <FacilitiesPanel
        open={openCard === "academic_facilities"}
        onClose={close}
        campusId={activeId}
        resource="facilities"
        category="academic"
        catalog={ACADEMIC_FACILITIES}
        title="Academic Facilities"
        subtitle="Enable and configure labs, libraries, seminar halls and more."
      />
      <FacilitiesPanel
        open={openCard === "sports_facilities"}
        onClose={close}
        campusId={activeId}
        resource="facilities"
        category="sports"
        catalog={SPORTS_FACILITIES}
        title="Sports Facilities"
        subtitle="Fields, courts, pools, gymnasium and athletics."
      />
      <FacilitiesPanel
        open={openCard === "student_facilities"}
        onClose={close}
        campusId={activeId}
        resource="facilities"
        category="student"
        catalog={STUDENT_FACILITIES}
        title="Student Facilities"
        subtitle="Transport, hostel, cafeteria, medical, lounges."
      />
      <FacilitiesPanel
        open={openCard === "smart_campus"}
        onClose={close}
        campusId={activeId}
        resource="smart"
        catalog={SMART_CAMPUS_ITEMS}
        title="Smart Campus"
        subtitle="WiFi, smart boards, RFID, biometric, IoT, GPS and apps."
        testidKey="smart"
      />
      <FacilitiesPanel
        open={openCard === "safety_security"}
        onClose={close}
        campusId={activeId}
        resource="safety"
        catalog={SAFETY_ITEMS}
        title="Safety & Security"
        subtitle="Fire safety, evacuation, accessibility and compliance."
        testidKey="safety"
      />
      <FacilitiesPanel
        open={openCard === "utilities"}
        onClose={close}
        campusId={activeId}
        resource="utilities"
        catalog={UTILITIES}
        title="Utilities"
        subtitle="Electricity, water, internet, waste and sustainability."
        testidKey="utilities"
      />
      <FacilitiesPanel
        open={openCard === "assets"}
        onClose={close}
        campusId={activeId}
        resource="assets"
        catalog={ASSET_KINDS}
        title="Assets"
        subtitle="Track computers, projectors, furniture and other physical assets by category."
        testidKey="assets"
      />
      <DocumentsPanel open={openCard === "documents"} onClose={close} campusId={activeId} />
      <MaintenancePanel open={openCard === "maintenance"} onClose={close} campusId={activeId} />
      <ReportsPanel open={openCard === "reports"} onClose={close} campusId={activeId} />
      {CARD_SPECS.filter((s) => !["campus_profile", "buildings", "academic_facilities", "sports_facilities", "student_facilities", "smart_campus", "safety_security", "utilities", "assets", "documents", "maintenance", "reports"].includes(s.key)).map((s) => (
        <ComingSoonPanel key={s.key} open={openCard === s.key} onClose={close} title={s.label} />
      ))}
    </AppShell>
  );
}

function StatusBadge({ pct }) {
  if (pct >= 90) return <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 uppercase tracking-wide">Complete</span>;
  if (pct > 0)  return <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-700 uppercase tracking-wide">In progress</span>;
  return          <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-500 uppercase tracking-wide">Not started</span>;
}

function Kpi({ icon: Icon, label, value, tone }) {
  const styles = COLOR_STYLES[tone] || COLOR_STYLES.slate;
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-3.5 flex items-center gap-3">
      <div className={cn("h-10 w-10 rounded-xl flex items-center justify-center shrink-0", styles.bg)}>
        <Icon className={cn("h-4 w-4", styles.text)} />
      </div>
      <div className="min-w-0">
        <div className="text-lg font-semibold text-slate-900" data-testid={`kpi-${label.toLowerCase().replace(/[^a-z]+/g,"-")}`}>
          {value ?? 0}
        </div>
        <div className="text-[11px] text-slate-500 truncate">{label}</div>
      </div>
    </div>
  );
}

function ProgressPill({ label, count, tone = "dark" }) {
  return (
    <div className={cn(
      "rounded-lg p-2 text-center",
      tone === "light" ? "bg-white/15 text-white" : "bg-slate-50 text-slate-700"
    )}>
      <div className="text-lg font-semibold">{count}</div>
      <div className="text-[10px] opacity-80 uppercase tracking-wide">{label}</div>
    </div>
  );
}

function sectionsByState(completions, state) {
  const vals = Object.values(completions || {});
  if (state === "done")        return vals.filter((v) => v >= 90).length;
  if (state === "in_progress") return vals.filter((v) => v > 0 && v < 90).length;
  return                        vals.filter((v) => v === 0).length;  // not_started
}

function ComplianceStrip({ expiring = {}, maintenance = {}, onOpen }) {
  const exp = expiring || {};
  const maint = maintenance || {};
  const totalExpiring = (exp.expired || 0) + (exp.in_30_days || 0) + (exp.in_60_days || 0) + (exp.in_90_days || 0);
  const openMaint = (maint.open || 0) + (maint.in_progress || 0);

  return (
    <section className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="compliance-strip">
      <button
        type="button"
        onClick={() => onOpen("documents")}
        className={cn(
          "text-left rounded-2xl p-5 border transition-all group",
          exp.expired > 0
            ? "bg-rose-50 border-rose-200 hover:border-rose-300"
            : totalExpiring > 0
              ? "bg-amber-50 border-amber-200 hover:border-amber-300"
              : "bg-emerald-50 border-emerald-200 hover:border-emerald-300"
        )}
        data-testid="expiring-widget"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <div className={cn(
              "h-10 w-10 rounded-xl flex items-center justify-center",
              exp.expired > 0 ? "bg-rose-100 text-rose-600" : totalExpiring > 0 ? "bg-amber-100 text-amber-600" : "bg-emerald-100 text-emerald-600"
            )}>
              {exp.expired > 0 ? <ShieldAlert className="h-5 w-5" /> : totalExpiring > 0 ? <Clock className="h-5 w-5" /> : <CheckCircle2 className="h-5 w-5" />}
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest font-semibold text-slate-500">Certificates</div>
              <div className="text-sm font-semibold text-slate-900">
                {exp.expired > 0
                  ? `${exp.expired} expired · action needed`
                  : totalExpiring > 0
                    ? `${totalExpiring} expiring soon`
                    : "All compliance certificates are current"}
              </div>
            </div>
          </div>
          <ArrowUpRight className="h-4 w-4 text-slate-400 group-hover:text-slate-600 transition-colors" />
        </div>
        <div className="mt-4 grid grid-cols-4 gap-2">
          <ExpBucket count={exp.expired} label="Expired" tone="rose" testid="exp-expired" />
          <ExpBucket count={exp.in_30_days} label="≤ 30 days" tone="rose" testid="exp-30" />
          <ExpBucket count={exp.in_60_days} label="31–60 days" tone="amber" testid="exp-60" />
          <ExpBucket count={exp.in_90_days} label="61–90 days" tone="amber" testid="exp-90" />
        </div>
      </button>

      <button
        type="button"
        onClick={() => onOpen("maintenance")}
        className="text-left rounded-2xl p-5 bg-white border border-slate-200 hover:border-slate-300 transition-all group"
        data-testid="maintenance-widget"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <div className="h-10 w-10 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center">
              <Wrench className="h-5 w-5" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest font-semibold text-slate-500">Maintenance</div>
              <div className="text-sm font-semibold text-slate-900">
                {openMaint > 0 ? `${openMaint} ticket${openMaint === 1 ? "" : "s"} open` : "No open tickets — great job!"}
              </div>
            </div>
          </div>
          <ArrowUpRight className="h-4 w-4 text-slate-400 group-hover:text-slate-600 transition-colors" />
        </div>
        <div className="mt-4 grid grid-cols-4 gap-2">
          <MaintBucket count={maint.open} label="Open" tone="amber" testid="maint-open" />
          <MaintBucket count={maint.in_progress} label="In Progress" tone="sky" testid="maint-progress" />
          <MaintBucket count={maint.resolved} label="Resolved" tone="emerald" testid="maint-resolved" />
          <MaintBucket count={maint.closed} label="Closed" tone="slate" testid="maint-closed" />
        </div>
      </button>
    </section>
  );
}

function ExpBucket({ count, label, tone, testid }) {
  const styles = tone === "rose"
    ? "bg-white/70 text-rose-700"
    : tone === "amber"
      ? "bg-white/70 text-amber-700"
      : "bg-white/70 text-emerald-700";
  return (
    <div className={cn("rounded-lg p-2 text-center", styles)} data-testid={testid}>
      <div className="text-xl font-bold">{count || 0}</div>
      <div className="text-[10px] uppercase tracking-wide opacity-80">{label}</div>
    </div>
  );
}

function MaintBucket({ count, label, tone, testid }) {
  const styles = {
    amber: "bg-amber-50 text-amber-700",
    sky: "bg-sky-50 text-sky-700",
    emerald: "bg-emerald-50 text-emerald-700",
    slate: "bg-slate-100 text-slate-600",
  }[tone];
  return (
    <div className={cn("rounded-lg p-2 text-center", styles)} data-testid={testid}>
      <div className="text-xl font-bold">{count || 0}</div>
      <div className="text-[10px] uppercase tracking-wide opacity-80">{label}</div>
    </div>
  );
}
